import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ConfigService } from '../services/config.service';
import { Resource, RestService } from '../services/rest.service';
import {
	BrandingConfig,
	BrandingCounts,
	BrandingSummaryState,
	PagedBrandingResponse,
	TenantBrandingMeResponse,
	TenantBrandingResponse,
	TenantBrandingSummary,
} from './branding.models';

/** Body for `POST /api/v1/tenant-branding/assets/upload-url`. */
export interface BrandingAssetUploadRequest {
	tenantId?: string;
	slot: string;
	contentType: string;
	size: number;
	fileName?: string;
}

/** Response from branding asset presign. */
export interface BrandingAssetUploadResponse {
	uploadUrl: string;
	assetUrl: string;
	objectKey: string;
	contentType: string;
	expiresAt: string;
}

/** Tenant Branding API client (Swagger tag `Tenant Branding`). GETs that return 204 emit `null`. */
@Injectable({ providedIn: 'root' })
export class BrandingApiService {
	private readonly moduleName = 'tenantBranding';
	private readonly configService = inject(ConfigService);
	private readonly restService = inject(RestService);

	/** Signed-in tenant's ACTIVE branding. Background call: no spinner, no error toast. */
	me(): Observable<TenantBrandingMeResponse | null> {
		const resource: Resource = { ...this.configService.getRestObject(this.moduleName, 'me'), skipLoading: true, skipErrorToast: true };
		return this.restService.execute<TenantBrandingMeResponse | null>(resource);
	}

	getActive(tenantId: string): Observable<TenantBrandingResponse | null> {
		return this.call('getActive', { urlParams: this.tenantQuery(tenantId) });
	}

	getDraft(tenantId: string, quiet = false): Observable<TenantBrandingResponse | null> {
		const resource: Resource = this.configService.getRestObject(this.moduleName, 'getDraft', { urlParams: this.tenantQuery(tenantId) });
		return this.restService.execute<TenantBrandingResponse | null>(quiet ? { ...resource, skipLoading: true, skipErrorToast: true } : resource);
	}

	getActiveQuiet(tenantId: string): Observable<TenantBrandingResponse | null> {
		const resource: Resource = this.configService.getRestObject(this.moduleName, 'getActive', { urlParams: this.tenantQuery(tenantId) });
		return this.restService.execute<TenantBrandingResponse | null>({ ...resource, skipLoading: true, skipErrorToast: true });
	}

	saveDraft(tenantId: string, branding: BrandingConfig, description?: string): Observable<TenantBrandingResponse> {
		return this.call('saveDraft', { body: this.body(tenantId, { branding, description }) });
	}

	/** Pre-signed S3 PUT for one logo/image slot. Client uploads bytes, then stores `assetUrl` in branding. */
	createAssetUploadUrl(request: BrandingAssetUploadRequest): Observable<BrandingAssetUploadResponse> {
		return this.call('createAssetUploadUrl', { body: request });
	}

	discardDraft(tenantId: string): Observable<null> {
		return this.call('discardDraft', { urlParams: this.tenantQuery(tenantId) });
	}

	/** Publishes `branding` when given (unsaved edits), otherwise the current draft. */
	publish(tenantId: string, branding?: BrandingConfig, description?: string): Observable<TenantBrandingResponse> {
		return this.call('publish', { body: this.body(tenantId, { branding, description }) });
	}

	listVersions(tenantId: string, page = 0, size = 50): Observable<PagedBrandingResponse<TenantBrandingResponse>> {
		return this.call('listVersions', {
			urlParams: { ...this.tenantQuery(tenantId), status: 'ALL', page, size, sort: 'version,desc' },
		});
	}

	rollback(id: string, description?: string): Observable<TenantBrandingResponse> {
		return this.call('rollback', { params: { id }, body: description?.trim() ? { description: description.trim() } : {} });
	}

	reset(tenantId: string, description?: string): Observable<null> {
		return this.call('reset', { body: this.body(tenantId, { description }) });
	}

	counts(): Observable<BrandingCounts> {
		return this.call('counts');
	}

	summary(params: { page?: number; size?: number; state?: BrandingSummaryState | 'ALL'; q?: string } = {}): Observable<PagedBrandingResponse<TenantBrandingSummary>> {
		const urlParams: Record<string, string | number> = {
			page: params.page ?? 0,
			size: params.size ?? 50,
			sort: 'tenantName,asc',
		};
		if (params.state && params.state !== 'ALL') urlParams['state'] = params.state;
		if (params.q?.trim()) urlParams['q'] = params.q.trim();
		return this.call('summary', { urlParams });
	}

	private call<T>(name: string, paramsAndBody?: Record<string, unknown>): Observable<T> {
		return this.restService.execute<T>(this.configService.getRestObject(this.moduleName, name, paramsAndBody));
	}

	private tenantQuery(tenantId: string): Record<string, string> {
		return tenantId?.trim() ? { tenantId: tenantId.trim() } : {};
	}

	private body(tenantId: string, extra: { branding?: BrandingConfig; description?: string }): Record<string, unknown> {
		const body: Record<string, unknown> = {};
		if (tenantId?.trim()) body['tenantId'] = tenantId.trim();
		if (extra.branding) body['branding'] = extra.branding;
		if (extra.description?.trim()) body['description'] = extra.description.trim();
		return body;
	}
}
